import React, { useState, useEffect, useContext } from 'react';
import Iller from "../../il-ilce.json";
import Modal from 'react-modal';
import './DeliveryForm.css';
import { InstitutionalContext } from '../../context/InstitutionalContext';
import { useToast } from '../../context/ToastContext';
import { useGetAddressesQuery } from '../../services/userApi';

const DeliveryForm = ({
  referansKodu,
  onSubmitSuccess,
  isDepositInCart = false
}) => {
  const { fetchInstitutional } = useContext(InstitutionalContext);
  const { showToast } = useToast();
  const [formErrors, setFormErrors] = useState({});
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentTerm, setCurrentTerm] = useState('');
  const [termContent, setTermContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Sözleşme durum state'leri kendi içimizde tanımlandı
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [salesTermsAccepted, setSalesTermsAccepted] = useState(false);
  const [deliveryTermsAccepted, setDeliveryTermsAccepted] = useState(false);
  
  // formData state'i artık kendi içimizde tanımlanıyor
  const [formData, setFormData] = useState({
    ad: '',
    soyad: '',
    email: '',
    telefon: '',
    il: '',
    ilce: '',
    adres: '',
    siparis_notu: '',
    paymentType: 'creditCard'
  });
  
  // Kullanıcı giriş durumunu kontrol et
  useEffect(() => {
    const token = localStorage.getItem('token');
    // const tkn = localStorage.getItem('tkn'); // tkn kontrolü de gerekebilir, şimdilik sadece token'a bakılıyor
    setIsLoggedIn(!!token); // Sadece token varlığına göre isLoggedIn ayarlanıyor
  }, []);

  // iller state'ini Iller.json'dan doldur
  useEffect(() => {
    setIller(Iller.iller);
  }, []);

  // RTK Query ile adresleri çek
  const { 
    data: addressesData, 
    isLoading: addressesLoading, 
    error: addressesError 
  } = useGetAddressesQuery(undefined, { skip: !isLoggedIn });

  // addresses state'ini RTK Query'den gelen veriyle güncelle
  const addresses = Array.isArray(addressesData) ? addressesData : [];
  
  useEffect(() => {
    // Kayıtlı adresleri getir useEffect'i RTK Query tarafından yönetildiği için kaldırıldı veya basitleştirildi.
    // Hata yönetimi eklenebilir
    if (addressesError) {
      console.error("Adresler yüklenirken hata oluştu:", addressesError);
      showToast("Adresler yüklenirken bir hata oluştu", "error");
    }
  }, [isLoggedIn, addressesError, showToast]);

  const handleAddressSelect = (e) => {
    const addressId = e.target.value;
    setSelectedAddressId(addressId);

    if (!addressId) {
      // Boş seçim yapıldıysa formu temizle
      setFormData(prev => ({
        ...prev,
        ad: '',
        soyad: '',
        email: '',
        telefon: '',
        il: '',
        ilce: '',
        adres: ''
      }));
      return;
    }

    // Seçilen adresi bul
    const selectedAddress = addresses.find(address => address.id.toString() === addressId);
    if (selectedAddress) {
      // İl seçildiğinde ilçe listesini güncelle
      const secilenIl = iller.find((il) => il.il_adi === selectedAddress.il);
      setIlceler(secilenIl ? secilenIl.ilceler : []);

      // Formu doldur
      setFormData(prev => ({
        ...prev,
        ad: selectedAddress.ad || '',
        soyad: selectedAddress.soyad || '',
        email: selectedAddress.email || '',
        telefon: selectedAddress.telefon || '',
        il: selectedAddress.il || '',
        ilce: selectedAddress.ilce || '',
        adres: selectedAddress.addressText || ''
      }));
    }
  };

  const termMap = {
    'kullanım-kosullari': 'kullanim-kosullari',
    'mesafeli-satis': 'mesafeli-satis-sozlesmesi',
    'teslimat-iade': 'teslimat-ve-iade'
  };

  const getTermsContent = async (sef) => {
    try {
      const mappedTerm = termMap[sef];
      if (!mappedTerm) {
        throw new Error('Geçersiz terim');
      }

      const content = await fetchInstitutional(mappedTerm);

      if (!content || !content.baslik || !content.aciklama) {
        throw new Error('İçerik bulunamadı');
      }

      return (
        <div>
          <h2>{content.baslik}</h2>
          <div dangerouslySetInnerHTML={{ __html: content.aciklama }} />
        </div>
      );
    } catch (error) {
      console.error('Sözleşme içeriği yüklenirken hata:', error);
      return (
        <div>
          <p>Sözleşme içeriği yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.</p>
        </div>
      );
    }
  };

  useEffect(() => {
    const loadTermContent = async () => {
      if (modalIsOpen && currentTerm) {
        setIsLoading(true);
        try {
          const content = await getTermsContent(currentTerm);
          setTermContent(content);
        } catch (error) {
          console.error('Sözleşme içeriği yüklenirken hata:', error);
          setTermContent(<div>Sözleşme içeriği yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.</div>);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadTermContent();
  }, [modalIsOpen, currentTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatPhoneNumber = (number) => {
    return number.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
  };

  const handleIlChange = (event) => {
    const ilSecimi = event.target.value;
    setFormData(prev => ({ ...prev, il: ilSecimi, ilce: '' }));
    const secilenIl = iller.find((il) => il.il_adi === ilSecimi);
    setIlceler(secilenIl ? secilenIl.ilceler : []);
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Ad validation
    if (!formData.ad.trim()) {
      errors.ad = "Ad alanı zorunludur";
      isValid = false;
    } else if (formData.ad.trim().length < 2) {
      errors.ad = "Ad en az 2 karakter olmalıdır";
      isValid = false;
    }

    // Soyad validation
    if (!formData.soyad.trim()) {
      errors.soyad = "Soyad alanı zorunludur";
      isValid = false;
    } else if (formData.soyad.trim().length < 2) {
      errors.soyad = "Soyad en az 2 karakter olmalıdır";
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = "Email alanı zorunludur";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = "Geçerli bir email adresi giriniz";
        isValid = false;
      }
    }

    // Telefon validation
    if (!formData.telefon || formData.telefon.length < 10) {
      errors.telefon = "Geçerli bir telefon numarası giriniz";
      isValid = false;
    }

    // İl validation
    if (!formData.il) {
      errors.il = "Lütfen bir il seçiniz";
      isValid = false;
    }

    // İlçe validation
    if (!formData.ilce) {
      errors.ilce = "Lütfen bir ilçe seçiniz";
      isValid = false;
    }

    // Adres validation
    if (!formData.adres.trim()) {
      errors.adres = "Adres alanı zorunludur";
      isValid = false;
    } else if (formData.adres.trim().length < 10) {
      errors.adres = "Adres en az 10 karakter olmalıdır";
      isValid = false;
    }

    setFormErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors)[0];
      if (firstError) {
        showToast(firstError, "error");
      }
    }

    return isValid;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Sözleşme kabul kontrolü
    if (!termsAccepted || !salesTermsAccepted || !deliveryTermsAccepted) {
      showToast("Lütfen tüm sözleşmeleri kabul edin.", "info");
      return;
    }

    // Form validasyonu
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return;
    }

    // Form geçerliyse, Card.js'e başarılı durumu bildir
    try {
      onSubmitSuccess(formData);
    } catch (error) {
      console.error('Form gönderme hatası:', error);
      showToast('İşlem sırasında bir hata oluştu', 'error');
    }
  };

  return (
    <div className='order-summary-second'>
      <form onSubmit={handleFormSubmit} className="checkout-form">
        <div className="checkout-form-group">
          <div className='siparis-adres-baslik'>
            <h3>Teslimat Bilgileri</h3>
          </div>
        </div>

        {/* Kayıtlı Adresler Dropdown */}
        {isLoggedIn && Array.isArray(addresses) && addresses.length > 0 && (
          <div className="checkout-form-group mb-4">
            <select
              name="kayitliAdres"
              value={selectedAddressId}
              onChange={handleAddressSelect}
              disabled={addressesLoading}
              className="saved-address-select"
            >
              <option value="">Kayıtlı Adreslerim</option>
              {addresses.map((address) => (
                <option key={address.id} value={address.id}>
                  {address.title || `${address.il || ''} ${address.ilce || ''}`} {address.il ? ` - ${address.il}` : ''} {address.ilce || ''}
                </option>
              ))}
            </select>
            {addressesLoading && <span className="loading-text">Adresler yükleniyor...</span>}
          </div>
        )}

        <div className="checkout-form-group d-flex flex-row flex-nowrap">
          <div className=''>
            <label>Ad*</label>
            <input
              type="text"
              name="ad"
              value={formData.ad}
              onChange={handleInputChange}
              placeholder="Ad*"
              className={formErrors.ad ? "input-error" : "w-100"}
              required
            />
            {formErrors.ad && <span className="error">*{formErrors.ad}</span>}
          </div>
          <div>
            <label>Soyad*</label>
            <input
              type="text"
              name="soyad"
              value={formData.soyad}
              onChange={handleInputChange}
              placeholder="Soyad*"
              className={formErrors.soyad ? "input-error" : "w-100"}
              required
            />
            {formErrors.soyad && <span className="error">*{formErrors.soyad}</span>}
          </div>
        </div>

        <div className="checkout-form-group d-flex flex-row flex-nowrap">
          <div>
            <label>Email*</label>
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email*"
              className={formErrors.email ? "input-error" : "w-100"}
              required
            />
            {formErrors.email && <span className="error">*{formErrors.email}</span>}
          </div>
          <div>
            <label>Telefon*</label>
            <input
              type="tel"
              name="telefon"
              value={formatPhoneNumber(formData.telefon)}
              onChange={handleInputChange}
              placeholder="Telefon*"
              maxLength="15"
              className={formErrors.telefon ? "input-error" : "w-100"}
              required
            />
            {formErrors.telefon && <span className="error">*{formErrors.telefon}</span>}
          </div>
        </div>

        <div className="checkout-form-group d-flex flex-row flex-nowrap">
          <select
            name="il"
            value={formData.il}
            onChange={handleIlChange}
            className={formErrors.il ? "input-error" : "w-100"}
            required
          >
            <option value="">Lütfen Bir İl Seçiniz</option>
            {iller.map((il, index) => (
              <option key={index} value={il.il_adi}>
                {il.il_adi}
              </option>
            ))}
          </select>
          {formErrors.il && <span className="error">*{formErrors.il}</span>}

          <select
            name="ilce"
            value={formData.ilce}
            onChange={handleInputChange}
            className={formErrors.ilce ? "input-error" : "w-100"}
            required
            disabled={!formData.il}
          >
            <option value="">Lütfen Bir İlçe Seçiniz</option>
            {ilceler.map((ilce, index) => (
              <option key={index} value={ilce.ilce_adi}>
                {ilce.ilce_adi}
              </option>
            ))}
          </select>
          {formErrors.ilce && <span className="error">*{formErrors.ilce}</span>}
        </div>

        <div className="checkout-form-group">
          <label>Sipariş Adresiniz*</label>
          <textarea
            name="adres"
            value={formData.adres}
            onChange={handleInputChange}
            placeholder="Sipariş Adresiniz"
            className={formErrors.adres ? "input-error" : ""}
            required
          />
          {formErrors.adres && <span className="error">*{formErrors.adres}</span>}
        </div>

        <div className="checkout-form-group">
          <label>Sipariş Notunuz</label>
          <textarea
            name="siparis_notu"
            value={formData.siparis_notu}
            onChange={handleInputChange}
            placeholder="Sipariş Notunuz"
          />
        </div>

        <div className="payment-method-options">
          <h3>Ödeme Yöntemi</h3>
          <div className="payment-radio-container">
            <div className="payment-radio">
              <input
                type="radio"
                id="creditCard"
                name="paymentType"
                value="creditCard"
                checked={formData.paymentType === 'creditCard'}
                onChange={handleInputChange}
              />
              <label htmlFor="creditCard">Kredi Kartı ile Ödeme</label>
            </div>
            <div className="payment-radio">
              <input
                type="radio"
                id="eft"
                name="paymentType"
                value="eft"
                checked={formData.paymentType === 'eft'}
                onChange={handleInputChange}
              />
              <label htmlFor="eft">Havale/EFT ile Ödeme </label>
              {/* <span className='text-danger' style={{fontSize:'14px'}}>(%2 indirim)</span> */}
              
            </div>
          </div>
        </div>

        {referansKodu && (
          <div className="checkout-form-group ref-code" style={{ display: 'none' }}>
            <label>Referans Kodu</label>
            <input
              type="hidden"
              name="referansKodu"
              value={referansKodu}
            />
            <p className="referans-info">Bu sepet sizinle paylaşıldı. Referans kodu otomatik olarak kaydedilmiştir.</p>
          </div>
        )}

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="kullanim-kosullari"
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
          />
          <label htmlFor="kullanim-kosullari">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('kullanım-kosullari');
              setModalIsOpen(true);
            }}>Kullanım Koşulları</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="mesafeli-satis"
            checked={salesTermsAccepted}
            onChange={() => setSalesTermsAccepted(!salesTermsAccepted)}
          />
          <label htmlFor="mesafeli-satis">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('mesafeli-satis');
              setModalIsOpen(true);
            }}>Mesafeli Satış</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="teslimat-iade"
            checked={deliveryTermsAccepted}
            onChange={() => setDeliveryTermsAccepted(!deliveryTermsAccepted)}
          />
          <label htmlFor="teslimat-iade">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('teslimat-iade');
              setModalIsOpen(true);
            }}>Teslimat ve İade</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <button type="submit" className='button-apply' disabled={isDepositInCart}>
          {isDepositInCart ? 'Kaporalı ürün var' : 'Siparişi Tamamla'}
        </button>
      </form>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => {
          setModalIsOpen(false);
          setCurrentTerm('');
          setTermContent(null);
        }}
        contentLabel="Sözleşme Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <button
          className="close-button"
          onClick={() => {
            setModalIsOpen(false);
            setCurrentTerm('');
            setTermContent(null);
          }}
        >
          ×
        </button>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Sözleşme içeriği yükleniyor...</p>
          </div>
        ) : (
          termContent
        )}
      </Modal>
    </div>
  );
};

export default DeliveryForm; 